import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'

import { createRetainer } from '@/lib/live/retainer'
import { useBrandStore } from '@/stores/brand'
import { useSetupPositionStore } from '@/stores/setup-position'
import { SETUP_STEP_SOURCES, type SetupStepSource } from '@/stores/setup-steps'
import {
  BUILT_ONBOARDING_CHAIN,
  ONBOARDING_CHAIN_STEPS,
  type OnboardingChainConfirmations,
  type OnboardingChainStep,
  type OnboardingChainStepView,
  chainDone,
} from '@/types/onboarding-chain'
import { strongestActivity } from '@/types/activity'
import { isError, isLoaded } from '@/types/load-state'

function stepSource(step: OnboardingChainStep): SetupStepSource | null {
  return SETUP_STEP_SOURCES[step]?.() ?? null
}

function builtSources(): SetupStepSource[] {
  return BUILT_ONBOARDING_CHAIN.flatMap((step) => {
    const source = stepSource(step)
    return source === null ? [] : [source]
  })
}

export const useSetupStore = defineStore('setup', () => {
  const positionStore = useSetupPositionStore()
  const completedInSession = ref(false)

  const freshSteps = ref<ReadonlySet<OnboardingChainStep>>(new Set())

  const changingSteps = ref<ReadonlySet<OnboardingChainStep>>(new Set())

  function openSources(): void {
    for (const source of builtSources()) source.open()
  }

  function closeSources(): void {
    for (const source of builtSources()) source.close()
    positionStore.reset()
    freshSteps.value = new Set()
    changingSteps.value = new Set()
    completedInSession.value = false
  }

  const sourcesRetainer = createRetainer({ open: openSources, close: closeSources })
  onScopeDispose(sourcesRetainer.dispose)

  const readFailed = computed(
    () => isError(useBrandStore().brand) || builtSources().some((source) => source.readFailed),
  )

  function retryReads(): void {
    useBrandStore().reopenFailed()
    closeSources()
    openSources()
  }

  const done = computed(() => {
    const confirmations: OnboardingChainConfirmations = Object.fromEntries(
      BUILT_ONBOARDING_CHAIN.flatMap((step) => {
        const source = stepSource(step)
        return source === null ? [] : [[step, source.confirmation]]
      }),
    )
    return chainDone(confirmations)
  })

  function stepFinished(step: OnboardingChainStep): boolean {
    return done.value[step]
  }

  function isLocked(step: OnboardingChainStep): boolean {
    return stepFinished(step) && !changingSteps.value.has(step)
  }

  function startChange(step: OnboardingChainStep): void {
    changingSteps.value = new Set([...changingSteps.value, step])
  }

  function endChange(step: OnboardingChainStep): void {
    const next = new Set(changingSteps.value)
    next.delete(step)
    changingSteps.value = next
  }

  const currentStep = computed<OnboardingChainStep>(
    () =>
      BUILT_ONBOARDING_CHAIN.find((step) => !stepFinished(step)) ??
      BUILT_ONBOARDING_CHAIN[BUILT_ONBOARDING_CHAIN.length - 1] ??
      ONBOARDING_CHAIN_STEPS[0],
  )

  const stepStates = computed<OnboardingChainStepView[]>(() =>
    ONBOARDING_CHAIN_STEPS.map((step, index) => ({
      step,
      index,
      built: BUILT_ONBOARDING_CHAIN.includes(step),
      state: stepFinished(step) ? 'done' : step === currentStep.value ? 'now' : 'todo',
      fresh: freshSteps.value.has(step),
    })),
  )

  const activity = computed(() =>
    strongestActivity(builtSources().map((source) => source.activity)),
  )

  const resolved = computed(
    () =>
      useBrandStore().brand.status !== 'loading' &&
      builtSources().every((source) => source.resolved),
  )

  watch(
    () => ({
      ready: resolved.value && !readFailed.value,
      finished: BUILT_ONBOARDING_CHAIN.filter(stepFinished),
    }),
    (now, before) => {
      if (!now.ready || !before.ready) return
      const turned = now.finished.filter((step) => !before.finished.includes(step))
      if (turned.length > 0) freshSteps.value = new Set([...freshSteps.value, ...turned])
    },
  )

  const isComplete = computed(() => {
    const brand = useBrandStore().brand
    if (!resolved.value || readFailed.value) return false
    return isLoaded(brand) && brand.value.onboardingStatus === 'complete'
  })

  const needsSetup = computed(() => {
    if (!resolved.value) return false
    if (readFailed.value) return true
    const brand = useBrandStore().brand
    if (!isLoaded(brand)) return false
    return brand.value.onboardingStatus !== 'complete'
  })

  function enter(step: OnboardingChainStep): void {
    if (positionStore.position?.step === step) return
    positionStore.show({ step, index: stepSource(step)?.resumeIndex() ?? 0 })
  }

  const position = computed(() => positionStore.position)

  const motion = computed(() => positionStore.motion)

  const canGoBack = computed(() => positionStore.canGoBack)

  function back(): void {
    positionStore.back()
  }

  /** Moves to the step the founder is on, on the screen its saved data picks; false when that step is already on show. */
  function returnToCurrent(): boolean {
    const step = currentStep.value
    if (positionStore.position?.step === step) return false
    positionStore.jump({ step, index: stepSource(step)?.resumeIndex() ?? 0 })
    return true
  }

  function goToNextStep(): boolean {
    return positionStore.goToNextStep((step) => stepSource(step)?.resumeIndex() ?? 0)
  }

  function handOver(): void {
    completedInSession.value = true
  }

  return {
    completedInSession,
    currentStep,
    stepStates,
    position,
    motion,
    canGoBack,
    resolved,
    readFailed,
    isComplete,
    needsSetup,
    activity,
    retain: sourcesRetainer.retain,
    retryReads,
    enter,
    back,
    goToNextStep,
    returnToCurrent,
    isLocked,
    startChange,
    endChange,
    handOver,
  }
})
